// eslint-disable-next-line no-restricted-imports -- トグル押下状態 (押下/非押下で地と枠が動的に変わる) は固定 variant の named DS button で表現できないため、生 Button の使用をこの共通部品に集約する
import { Button, type ButtonProps } from '@enechain/ecloud-designsystem'

type Props = Omit<ButtonProps, 'variant' | 'colorPalette'> & {
  /** 現在の押下状態。 true = 選択中 (DS アクセント色の塗り)、false = 非選択 (DS Tertiary 相当)。 */
  pressed: boolean
}

/**
 * トグル押下状態 (選択中/非選択) を表現する共通ボタン。
 *
 * `aria-pressed` は常時付与する (= 個別呼び出し側での付け忘れ防止)。
 *
 * ## chakra の variant を使わず、DS の宣言値を直接当てる
 *
 * 以前は `variant="outline"` + `colorPalette="gray"` (非押下) /
 * `variant="solid"` + `colorPalette="blue"` (押下) だったが、 **どちらも DS が
 * 定義していない chakra 素の variant** で、 DS の named button と見た目が揃わない
 * (デザイナー指摘 2026-08-12「当日と翌日のボタンが DS 違反 / border が太い」)。
 *
 * variant は `ghost` (= 枠も地も持たない) に固定し、 枠・地・文字色を **DS が
 * `TertiaryButton` / アクセント色で宣言している値そのもの**で明示する。
 * variant 由来の枠が乗らないので、 枠は常にここで書いた 1px だけになる。
 *
 * | 状態 | 地 | 枠 | 文字 | 出典 |
 * |---|---|---|---|---|
 * | 非押下 | `Surface.primary` | 1px `Border.midEmphasis` | `Text.highEmphasis` | DS `TertiaryButton` の宣言値 |
 * | 押下 | `Surface.accentPrimary` | 1px 同色 | `Text.highEmphasisInverse` | DS `PrimaryButton` / 8080 `[aria-pressed="true"]` |
 *
 * 押下色を `colorPalette="blue"` に任せていた時は chakra の `blue.solid` =
 * `blue.600` = #1714eb を拾っており、 DS のアクセント色 #0c00c5 (= blue.700) より
 * 1 段明るい別の青になっていた。
 *
 * ## named DS button に置き換えていない理由
 *
 * 非押下を `TertiaryButton` そのものにすると見た目は一致するが、 **DS の named
 * button は `disabled` を native ではなく `aria-disabled` の soft-disable で
 * 表現する**ため、 `disabled` を渡している既存の呼び出し側 (相対取引の商品形態
 * トグル等) で挙動が変わる (実測: 既存 test 3 件が落ちる)。 見た目だけを DS に
 * 揃え、 挙動は生 `Button` のまま保つ。
 */
export const ToggleButton = ({
  pressed,
  ...props
}: Props): React.ReactElement => {
  return (
    <Button
      {...props}
      color={pressed ? 'Text.highEmphasisInverse' : 'Text.highEmphasis'}
      borderWidth="1px"
      borderStyle="solid"
      borderColor={pressed ? 'Surface.accentPrimary' : 'Border.midEmphasis'}
      _hover={{
        backgroundColor: pressed
          ? 'Hover.accentPrimaryHigh'
          : 'Hover.lowEmphasis',
      }}
      aria-pressed={pressed}
      backgroundColor={pressed ? 'Surface.accentPrimary' : 'Surface.primary'}
      variant="ghost"
    />
  )
}
